import { Controller } from 'react-hook-form';
import { MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/FormDialog';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { LookupViaCepButton } from '../../../shared/components/LookupViaCepButton';
import { UFS } from '../../../shared/data/brasil';
import { clientAddressInputSchema, type ClientAddressFormValues } from './schemas';
import type { CepLookupResult, ClientAddressDto, ClientAddressInput } from './types';

export interface ClientAddressFormDialogProps {
  open: boolean;
  address: ClientAddressDto | null;
  hooks: {
    useCreate: () => UseMutationResult<ClientAddressDto, Error, ClientAddressInput>;
    useUpdate: () => UseMutationResult<
      ClientAddressDto,
      Error,
      { id: number; input: ClientAddressInput }
    >;
    useLookupCep: () => UseMutationResult<CepLookupResult, Error, string>;
  };
  onClose: () => void;
}

const DEFAULT_VALUES: ClientAddressFormValues = {
  cep: '',
  logradouro: '',
  numero: '',
  complemento: null,
  bairro: '',
  cidade: '',
  uf: '',
  ibge: '',
  ativo: true,
};

export function ClientAddressFormDialog({
  open,
  address,
  hooks,
  onClose,
}: ClientAddressFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const lookupMut = hooks.useLookupCep();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    ClientAddressFormValues,
    ClientAddressDto,
    ClientAddressInput,
    number
  >({
    open,
    entity: address,
    schema: clientAddressInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (a) => ({
      cep: a.cep,
      logradouro: a.logradouro,
      numero: a.numero,
      complemento: a.complemento,
      bairro: a.bairro,
      cidade: a.cidade,
      uf: a.uf,
      ibge: a.ibge ?? '',
      ativo: a.ativo,
    }),
    getId: (a) => a.id,
    hooks: { useCreate: hooks.useCreate, useUpdate: hooks.useUpdate },
    messages: {
      created: t('features.clientes.enderecos.toasts.created'),
      updated: t('features.clientes.enderecos.toasts.updated'),
    },
    focusField: 'cep',
    onClose,
  });

  const handleLookup = async (cepDigits: string): Promise<void> => {
    const r = await lookupMut.mutateAsync(cepDigits);
    form.setValue('logradouro', r.logradouro, { shouldValidate: true });
    form.setValue('bairro', r.bairro, { shouldValidate: true });
    form.setValue('cidade', r.cidade, { shouldValidate: true });
    form.setValue('uf', r.uf, { shouldValidate: true });
    form.setValue('ibge', r.ibge, { shouldValidate: true });
  };

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.clientes.enderecos.editTitle')
          : t('features.clientes.enderecos.createTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <Controller
          control={form.control}
          name="cep"
          render={({ field, fieldState }) => (
            <LookupViaCepButton
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
              onLookup={handleLookup}
              loading={lookupMut.isPending}
              error={fieldState.error?.message}
            />
          )}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {/*
            Campos preenchidos pelo ViaCEP (logradouro/bairro/cidade/ibge) usam
            `Controller` em vez de `register`: com `register` o TextField fica
            uncontrolled e o `setValue` do lookup escreve no DOM sem notificar o
            MUI, então o label não sobe (`shrink`) e sobrepõe o texto.
          */}
          <Controller
            control={form.control}
            name="logradouro"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.clientes.enderecos.fields.logradouro')}
                fullWidth
                inputProps={{ maxLength: 150 }}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <TextField
            label={t('features.clientes.enderecos.fields.numero')}
            sx={{ maxWidth: 140 }}
            inputProps={{ maxLength: 20 }}
            error={Boolean(form.formState.errors.numero)}
            helperText={form.formState.errors.numero?.message}
            {...form.register('numero')}
          />
        </Stack>

        <TextField
          label={t('features.clientes.enderecos.fields.complemento')}
          fullWidth
          inputProps={{ maxLength: 100 }}
          error={Boolean(form.formState.errors.complemento)}
          helperText={form.formState.errors.complemento?.message}
          {...form.register('complemento')}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="bairro"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.clientes.enderecos.fields.bairro')}
                fullWidth
                inputProps={{ maxLength: 100 }}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={form.control}
            name="cidade"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.clientes.enderecos.fields.cidade')}
                fullWidth
                inputProps={{ maxLength: 100 }}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="uf"
            render={({ field, fieldState }) => (
              <TextField
                select
                label={t('features.clientes.enderecos.fields.uf')}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="">—</MenuItem>
                {UFS.map((uf) => (
                  <MenuItem key={uf} value={uf}>
                    {uf}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={form.control}
            name="ibge"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.clientes.enderecos.fields.ibge')}
                sx={{ maxWidth: 160 }}
                inputProps={{ maxLength: 7 }}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ?? t('features.clientes.enderecos.fields.ibgeHelper')
                }
              />
            )}
          />
        </Stack>
      </Stack>
    </FormDialog>
  );
}
